import { isFrontDesk, type BusinessRole } from "./enums.ts";
import { frontDeskAccessOf, frontDeskItems, type FrontDeskAccess, type FrontDeskItem } from "./front-desk-access.ts";

/**
 * Who may call which business path: the one permission map of the team roles. The API guards each
 * route with it (`apps/desk-api/src/middleware/role.ts`, `routes/club.ts`), and Desk and the phone
 * read it through `deskMay` to show a button only to a person the API lets do the job.
 *
 * The owner and an admin may call every path. Staff and the front desk may call only the paths
 * named here: deny by default, so a new route is owner only until someone opens it on purpose.
 */

/** One path a role may call: the method and the part of the path after the business id. */
export type StaffRule = { method: string; path: RegExp };

/** The two route prefixes: `/owner/businesses/:id/...` and `/business/businesses/:id/...`. */
export type DeskScope = "owner" | "business";

/**
 * The paths under `/owner/businesses/:id/...` that staff may call. Staff run sessions: they read the
 * business (its words, shape and settings; the API leaves out the payout account and the join
 * code), the week, the roster and the groups, and they open a thread with a member. Nothing else.
 *
 * One list for every router that mounts `/owner/businesses/:id/*`, so the owner routes and the
 * messaging routes cannot disagree about a path.
 */
export const STAFF_OWNER_PATHS: StaffRule[] = [
  { method: "GET", path: /^$/ },
  { method: "GET", path: /^sessions$/ },
  { method: "GET", path: /^sessions\/[^/]+\/attendees$/ },
  { method: "GET", path: /^groups$/ },
  { method: "GET", path: /^schedule-templates$/ },
  { method: "POST", path: /^conversations\/direct$/ },
];

/**
 * The first part of the paths under `/business/businesses/:id/...` that staff may read. "coach"
 * stays only for the /coach/today alias path; drop it once that alias is removed.
 */
export const STAFF_BUSINESS_READS: ReadonlySet<string> = new Set(["schedule", "staff", "coach", "sessions", "participants", "timetable", "covers"]);

/**
 * The writes staff may make under `/business/businesses/:id/...` (routes/club.ts): run a session,
 * say it runs late, change its staff, book a member in, mark who came, and add the names of their
 * own swimmers (`staff/participants...`) and the ones who came and were not booked
 * (`sessions/:id/extras`, routes/coach-participants.ts), answer "Parent wanted"
 * (`staff/parent-wanted/:id/answer`, migration 0046), and ask for, take, refuse or cancel a cover
 * (`sessions/:id/cover`, `covers/:id/take|decline|cancel`, routes/covers.ts, migration 0072). Reads are open to staff by
 * the path head (`STAFF_BUSINESS_READS`); these are the only writes.
 */
export const STAFF_BUSINESS_WRITE =
  /^(sessions\/[^/]+\/(running-late|staff|coach|bookings|attendance|cover|extras(\/[^/]+)?)|covers\/[^/]+\/(take|decline|cancel)|staff\/participants(\/[^/]+)?|staff\/parent-wanted\/[^/]+\/answer)$/;

/**
 * The permission map of the Front desk role (migration 0080, Pilates studio shape T1). The front
 * desk works the desk: it books for a client (and takes a booking off), records a manual payment
 * (an owed payment, a package sold at the desk, or the next month of a membership, M2, with its proof of payment), checks people in (attendance on every session)
 * and approves or declines a new client who asked to join. It reads what those jobs need: the
 * week, the clients with their balances, the packages it sells (`GET packages`, sign-off T22), its
 * To do list and the team names. Messages: like staff,
 * it reads and answers the threads with members in the Inbox (`messaging.ts` opens them to every
 * team role), and it opens a thread with one member. Every business shape gets the role. The matrix
 * test is `apps/desk-api/tests/front-desk-permissions.test.ts`.
 *
 * The owner opens more in Settings, Front desk (`settings.frontDesk`, `front-desk-access.ts`): each
 * item adds the paths of `FRONT_DESK_ITEM_PATHS`. The start values keep the payments list and the
 * Classes page open, and keep closed the owner's Home numbers (`home`, `home/open-spots`: the money
 * and the people of the whole business, review R1.1), the changes to a client's balance, packages
 * and details, and the messages to many clients (decision D66). It never changes packages, rules,
 * the timetable, the team, the Teamna plan, payouts or the bank account, and it does not refund.
 */

/**
 * Under `/owner/businesses/:id/...`, always: the staff reads and the thread with a member. The list
 * of classes (`GET groups`) stays open with the Classes item off: the Calendar, always on, names and
 * filters the sessions with it. The item gates the Classes page and the booking rules of a class.
 */
export const FRONTDESK_OWNER_PATHS: StaffRule[] = [...STAFF_OWNER_PATHS];

/** Under `/business/businesses/:id/...` (routes/club.ts, families.ts, coach-participants.ts), always. */
export const FRONTDESK_BUSINESS_PATHS: StaffRule[] = [
  {
    method: "GET",
    path: /^(schedule|timetable|team|sessions\/[^/]+|participants|participants\/[^/]+|clients|families|todo|queue|pending-members|packages)$/,
  },
  { method: "POST", path: /^sessions\/[^/]+\/bookings$/ },
  { method: "POST", path: /^bookings\/[^/]+\/cancel$/ },
  { method: "PUT", path: /^sessions\/[^/]+\/attendance$/ },
  { method: "POST", path: /^participants\/[^/]+\/(record-payment|sell-package|membership\/renewal\/record)$/ },
  { method: "GET", path: /^participants\/[^/]+\/package-start$/ },
  // The proof of a payment it records (migration 0084). To change or remove a proof later is the owner's and the admins'.
  { method: "POST", path: /^payment-proofs$/ },
  { method: "POST", path: /^pending-members\/[^/]+\/(approve|decline)$/ },
];

/**
 * The paths each Front desk item opens, by scope. An item with no path here (`team`) changes what an
 * answer holds (`frontDeskMay` in the route), not which paths answer.
 */
export const FRONT_DESK_ITEM_PATHS: Record<FrontDeskItem, { owner?: StaffRule[]; business?: StaffRule[] }> = {
  // The owner Home: the numbers, the renewals, the clients who left, and the open spots (D65).
  homeNumbers: { business: [{ method: "GET", path: /^home(\/open-spots)?$/ }] },
  // The Classes page: the list reads the groups and the timetable (always open), and a class its rules.
  classes: { owner: [{ method: "GET", path: /^groups\/[^/]+\/rules$/ }] },
  // Add, change, delete (archive) and merge a class, and its booking rules.
  classesEdit: {
    owner: [
      { method: "POST", path: /^groups$/ },
      { method: "PATCH", path: /^groups\/[^/]+$/ },
      { method: "POST", path: /^groups\/[^/]+\/merge$/ },
      { method: "PUT", path: /^groups\/[^/]+\/rules$/ },
    ],
  },
  payments: { owner: [{ method: "GET", path: /^payments$/ }] },
  // The Staff page shows the team list (always open); with this item it has the mobile numbers.
  team: {},
  adjustBalance: { business: [{ method: "POST", path: /^participants\/[^/]+\/adjust$/ }] },
  // One pack (freezes), and Freeze all for a client, a family or one child (freeze-many, D17).
  freeze: {
    business: [
      { method: "POST", path: /^participants\/[^/]+\/freezes(\/[^/]+\/end)?$/ },
      { method: "POST", path: /^participants\/[^/]+\/freeze-many(\/end)?$/ },
    ],
  },
  paymentRequests: { business: [{ method: "POST", path: /^participants\/[^/]+\/payment-requests(\/[^/]+\/cancel)?$/ }] },
  // A client's photo only: `requirePhotoRights` in routes/owner.ts refuses a team photo.
  photo: {
    owner: [
      { method: "POST", path: /^photos$/ },
      { method: "DELETE", path: /^photos\/participant\/[^/]+$/ },
    ],
  },
  // The client's Settings tab: the adults, the children, the group, the hold, remove and undo.
  editClient: {
    business: [
      { method: "PATCH", path: /^participants\/[^/]+$/ },
      { method: "POST", path: /^participants$/ },
      { method: "POST", path: /^families\/restore$/ },
      { method: "DELETE", path: /^families\/[^/]+$/ },
      { method: "POST", path: /^families\/[^/]+\/(adults|children)$/ },
      { method: "PATCH", path: /^families\/[^/]+\/(adults|children)\/[^/]+$/ },
      { method: "DELETE", path: /^families\/[^/]+\/(adults|children)\/[^/]+$/ },
      { method: "POST", path: /^families\/[^/]+\/children\/[^/]+\/restore$/ },
      { method: "PUT", path: /^families\/[^/]+\/hold$/ },
      { method: "DELETE", path: /^families\/[^/]+\/hold$/ },
    ],
  },
  addClients: {
    business: [
      { method: "POST", path: /^families$/ },
      { method: "POST", path: /^families\/undo$/ },
    ],
  },
  sendInvites: {
    business: [
      { method: "POST", path: /^families\/invite$/ },
      { method: "GET", path: /^families\/invites$/ },
    ],
  },
  // One message to many clients, and the messages Home sends (renewals, reminders to pay).
  messageClients: {
    business: [
      { method: "POST", path: /^clients\/message$/ },
      { method: "POST", path: /^nudges$/ },
    ],
  },
  exportClients: { business: [{ method: "GET", path: /^clients\/export$/ }] },
};

/** True when the front desk may use this item at a business with these settings (start values when unknown). */
export function frontDeskMay(access: Partial<FrontDeskAccess> | null | undefined, item: FrontDeskItem): boolean {
  return frontDeskAccessOf(access)[item];
}

/** The paths the front desk may call in one scope: the fixed list and the paths of each item that is on. */
export function frontDeskRules(scope: DeskScope, access?: Partial<FrontDeskAccess> | null): StaffRule[] {
  const on = frontDeskAccessOf(access);
  const fixed = scope === "owner" ? FRONTDESK_OWNER_PATHS : FRONTDESK_BUSINESS_PATHS;
  return [...fixed, ...frontDeskItems.filter((item) => on[item]).flatMap((item) => FRONT_DESK_ITEM_PATHS[item][scope] ?? [])];
}

/** True when a rule in `rules` opens this method and path. */
export function staffMay(rules: StaffRule[], method: string, rest: string): boolean {
  return rules.some((rule) => rule.method === method && rule.path.test(rest));
}

/**
 * True when a person with this role may call `method` on this path of the business: `rest` is the
 * part after `/businesses/:id/`, with no slash at either end. A member, or no role, may call none.
 * `frontDesk` is the business's Front desk setting (`settings.frontDesk`); only the front desk reads it.
 */
export function roleMayCall(
  role: BusinessRole | "member" | null | undefined,
  scope: DeskScope,
  method: string,
  rest: string,
  frontDesk?: Partial<FrontDeskAccess> | null,
): boolean {
  if (role === "owner" || role === "admin") return true;
  if (role !== "member" && isFrontDesk(role)) return staffMay(frontDeskRules(scope, frontDesk), method, rest);
  if (role !== "staff") return false;
  if (scope === "owner") return staffMay(STAFF_OWNER_PATHS, method, rest);
  return STAFF_BUSINESS_READS.has(rest.split("/")[0] ?? "") && (method === "GET" || STAFF_BUSINESS_WRITE.test(rest));
}

/**
 * The jobs on the owner screens that the API does not open to every team role, each with the route
 * it calls. A `:name` part is any id. The test `front-desk-permissions.test.ts` checks that each
 * route exists and that `deskMay` agrees with its list for the front desk.
 */
export const DESK_ACTIONS = {
  /** Add or remove sessions on a balance, by hand. */
  adjustBalance: { scope: "business", method: "POST", path: "participants/:id/adjust" },
  /** Freeze (pause) a package. */
  freezePackage: { scope: "business", method: "POST", path: "participants/:id/freezes" },
  /** End the pause of a package. */
  endPackageFreeze: { scope: "business", method: "POST", path: "participants/:id/freezes/:freezeId/end" },
  /** Freeze all: pause every pack of a client or a family, or of one child (D17). */
  freezeMany: { scope: "business", method: "POST", path: "participants/:id/freeze-many" },
  /** End every pause of a client or a family, or of one child (D17). */
  endManyPauses: { scope: "business", method: "POST", path: "participants/:id/freeze-many/end" },
  /** Move the last day of one package to a later day (card #1). Owner and admin only. */
  extendPackage: { scope: "business", method: "POST", path: "participants/:id/extend" },
  /** Move the unused sessions of one package to the package that waits for it (card #1). Owner and admin only. */
  carryOverPackage: { scope: "business", method: "POST", path: "participants/:id/carry-over" },
  /** Record the next month of a membership, paid in cash or by transfer (M2). */
  recordMembershipRenewal: { scope: "business", method: "POST", path: "participants/:id/membership/renewal/record" },
  /** Cancel a client's membership, at the end of the month or at once (M2). */
  cancelMembership: { scope: "business", method: "POST", path: "participants/:id/membership/cancel" },
  /** Ask a family for an amount (a one-off payment request). */
  requestPayment: { scope: "business", method: "POST", path: "participants/:id/payment-requests" },
  /** Cancel an open payment request. */
  cancelPaymentRequest: { scope: "business", method: "POST", path: "participants/:id/payment-requests/:requestId/cancel" },
  /** Attach a proof of payment to the cash or transfer payment it records now (migration 0084). */
  attachPaymentProof: { scope: "business", method: "POST", path: "payment-proofs" },
  /** Attach, replace or remove the proof of a payment recorded before. */
  changePaymentProof: { scope: "owner", method: "PUT", path: "payments/:id/proof" },
  /** Read the payments list (the Payments page, and Paid on a client page). */
  readPayments: { scope: "owner", method: "GET", path: "payments" },
  /** Read the owner Home numbers: money, renewals, the clients who left (D65). */
  readHomeNumbers: { scope: "business", method: "GET", path: "home" },
  /** Read the open spots of the next 48 hours (Home, H2). */
  readOpenSpots: { scope: "business", method: "GET", path: "home/open-spots" },
  /** Send the messages of Home: a renewal, a reminder to pay. */
  sendNudges: { scope: "business", method: "POST", path: "nudges" },
  /** Read the booking rules of one class (the Classes page). */
  readClassRules: { scope: "owner", method: "GET", path: "groups/:id/rules" },
  /** Add a class. */
  addClass: { scope: "owner", method: "POST", path: "groups" },
  /** Change or delete a class. */
  editClass: { scope: "owner", method: "PATCH", path: "groups/:id" },
  /** Merge classes with the same name. */
  mergeClasses: { scope: "owner", method: "POST", path: "groups/:id/merge" },
  /** Change a family: the adults, the children, the access, the hold, remove it (the Settings tab). */
  editFamily: { scope: "business", method: "PATCH", path: "families/:familyId/adults/:adultId" },
  /** Put a participant in a group, or confirm one a member added. */
  placeParticipant: { scope: "business", method: "PATCH", path: "participants/:id" },
  /** Take a participant off a family. */
  removeParticipant: { scope: "business", method: "DELETE", path: "families/:familyId/children/:participantId" },
  /** Add or change a profile photo. */
  setPhoto: { scope: "owner", method: "POST", path: "photos" },
  /** Add families or clients (paste, spreadsheet, one by one). */
  addFamilies: { scope: "business", method: "POST", path: "families" },
  /** Send the invites to join. */
  sendInvites: { scope: "business", method: "POST", path: "families/invite" },
  /** Export the clients list. */
  exportClients: { scope: "business", method: "GET", path: "clients/export" },
  /** Send one message to many clients. */
  messageClients: { scope: "business", method: "POST", path: "clients/message" },
  /** Link a name a coach typed to a family (the new participants). */
  linkNewParticipant: { scope: "business", method: "POST", path: "new-participants/:id/link" },
  /** Post an announcement to every member. */
  announce: { scope: "owner", method: "POST", path: "announcements" },
  /** Set up card payments (the payout account). */
  setUpCardPayments: { scope: "owner", method: "GET", path: "payment-onboarding" },
  /** Read the timetable editor: the groups, their colours and the team (the Week and Schedule views). */
  readTimetableEditor: { scope: "business", method: "GET", path: "timetable/editor" },
  /** Add a one-off session. */
  addSession: { scope: "owner", method: "POST", path: "sessions" },
  /** Move a session (a drag on the week, or Move session). */
  moveSession: { scope: "owner", method: "PATCH", path: "sessions/:id" },
  /** Cancel a session. */
  cancelSession: { scope: "owner", method: "POST", path: "sessions/:id/cancel" },
  /** Change who runs a session. */
  setSessionStaff: { scope: "business", method: "POST", path: "sessions/:id/staff" },
  /** Ask for a cover for a session. */
  askCover: { scope: "business", method: "POST", path: "sessions/:id/cover" },
  /** Add someone who came and was not booked ("Someone else came"). */
  addSessionExtra: { scope: "business", method: "POST", path: "sessions/:id/extras" },
} as const satisfies Record<string, { scope: DeskScope; method: string; path: string }>;

export type DeskAction = keyof typeof DESK_ACTIONS;

/**
 * True when a person with this role may do the job: the API's own rule for the job's route, with the
 * business's Front desk setting (`MyBusiness.frontDesk`) for the front desk. Desk and the phone read
 * it to show a button; the API still decides each call.
 */
export function deskMay(role: BusinessRole | "member" | null | undefined, action: DeskAction, frontDesk?: Partial<FrontDeskAccess> | null): boolean {
  const { scope, method, path } = DESK_ACTIONS[action];
  return roleMayCall(role, scope, method, path.replace(/:[A-Za-z]+/g, "id"), frontDesk);
}
